
import { Component, Vue, Ref, Watch } from "vue-property-decorator"
import { Action, Getter, State } from "vuex-class"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { CurrentUser } from "@/types/user"
import { BaseState } from "@/store"
import { BDropdown } from "bootstrap-vue"
import { CasesNotification, Urgency, notificationFunctions } from "@/types/notification"
import moment from "moment"
import NotificationsButton from "@/components/New/Page/Notifications/NotificationsButton.vue"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		"notifications-button": NotificationsButton,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class NotificationsMenu extends Vue {
	@State((state: BaseState) => state.screenWidth)	private screenWidth!: number;
	@State((state: BaseState) => state.me) private me!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getNotifications") public currentNotifications!: CasesNotification[];
	@Getter("getBrowserNotificationsPermission") public browserNotificationsEnabled!: boolean;
	@Getter("getHighestUrgency") public highestUrgency!: Urgency | null

	@Action requestBrowserNotificationsPermission!: () => void
	@Action processNotification!: (notification: CasesNotification) => void
	@Action clearUserNotifications!: () => void
	@Action markLocalNotificationsAsSent!: () => void

	@Ref() notificationsDropdown!: BDropdown

	public notificationTimePeriods: string[] = [];

	public get isNotificationActionsEnabled () {
		return this.getIsFeatureEnabled(TenantFeatures.NotificationActions)
	}

	public notificationListOpened () {
		this.updateNotificationTimePeriods()
	}

	public notificationListClosing () {
		this.markLocalNotificationsAsSent()
	}

	@Watch("currentNotifications")
	private currentNotificationsChanged (newNotifications: CasesNotification[]) {
		this.updateNotificationTimePeriods()
	}

	updateNotificationTimePeriods () {
		this.notificationTimePeriods = []
		let notifications: CasesNotification[] = this.currentNotifications
		notifications.forEach((notification) => {
			this.notificationTimePeriods.push(this.timeElapsedNotification(notification))
		})
	}

	/**
	 * Gets a human-readable version of the time elapsed since the notification was created
	 * Gives only the most significant time (e.g. seconds, minutes, hours & days)
	 * @param notification The notification to create the time elapsed message for
	 */
	public timeElapsedNotification (notification: CasesNotification): string {
		const notificationTime = new Date(moment(notification.created).format("l LTS"))
		const timeDifference = Date.now() - notificationTime.getTime()

		let days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
		let hours = Math.floor(Math.abs(timeDifference) / (1000 * 60 * 60) % 24)
		let minutes = Math.floor(Math.abs(timeDifference) / (1000 * 60) % 60)
		let seconds = Math.ceil(Math.abs(timeDifference) / (1000) % 60)

		let timeMessage = ""
		if (days === 1) {
			timeMessage = `${days} day`
		} else if (days > 1) {
			timeMessage = `${days} days`
		} else if (hours === 1) {
			timeMessage = `${hours} hour`
		} else if (hours > 1) {
			timeMessage = `${hours} hours`
		} else if (minutes === 1) {
			timeMessage = `${minutes} minute`
		} else if (minutes > 1) {
			timeMessage = `${minutes} minutes`
		} else if (seconds === 1) {
			timeMessage = `${seconds} second`
		} else if (seconds > 1) {
			timeMessage = `${seconds} seconds`
		} else {
			return "Just now"
		}

		return `${timeMessage} ago`
	}

	public enableBrowserNotifications (): void {
		this.requestBrowserNotificationsPermission()
	}

	public notificationSelected (notification: CasesNotification, event: MouseEvent) {
		if (event && event.target && (event.target instanceof HTMLAnchorElement)) {
			// Clicking links should not trigger the notificiation click
			return
		}

		this.processNotification(notification)
		this.notificationsDropdown.hide(true)
	}

	isNewNotification (notification: any): boolean {
		return !notification.sent ?? true
	}

	clearNotifications () {
		if (this.clearAllNotificationsFeatureEnabled) {
			this.clearUserNotifications()
		}
	}

	get clearAllNotificationsFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.NotificationsClearAllButton)
	}

	urgencyText (urgency: Urgency): string | null {
		return notificationFunctions.urgencyToString(urgency)
	}
}
